import React, {useEffect, useMemo, useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";
import "./AllProductsWhatsNew.css";
import {Setting, useSetting} from "contexts/setting";
import {Edits, useEdits} from "contexts/edits";
import {useImages} from "contexts/images";
import {PRODUCTS} from "data/products";
import {drawnClientTemplates} from "data/templates";
import {withSlotTemplateIds} from "data/templateSlots";
import {useTemplateSelection} from "hooks/useTemplateSelection";
import TemplateControls from "layout/TemplateControls";
import SortableList from "layout/SortableList";
import cn from "classnames";
import {findBgUrl} from "libs/bg-utils";
import {lookupBgImage} from "libs/bgCatalogIndex";
import {resolveOverride, useDefaultSceneOverrides} from "hooks/useDefaultSceneOverrides";
import {useCurrentSet} from "hooks/useCurrentSet";
import {useBgData} from "hooks/useBgData";
import {useBgCatalogIndex} from "hooks/useBgCatalogIndex";
import {useBgDerivation} from "hooks/useBgDerivation";
import {
  resolvedDefaultScenesAllQueryOptions,
  useResolvedDefaultScenesAll,
} from "hooks/useResolvedDefaultScenes";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {getFilteredReleaseTemplates, getReleaseProductCodesForType} from "utils/releases";
import {productInstanceAppearance} from "utils/allProductsScene";
import {withoutHiddenTemplates} from "libs/bannerComposition";
import {withoutDimensionModes} from "libs/sceneScope";

const ALL_PRODUCTS_TITLE_KEY_PREFIX = "all-products-whats-new-title";
const ALL_PRODUCTS_FEATURES_KEY_PREFIX = "all-products-whats-new-features";
const ALL_PRODUCTS_PRODUCT_NAME_KEY_PREFIX = "all-products-whats-new-product-name";
const ALL_PRODUCTS_BG_OPTIONS_FIELD = "allProductsBgOptions";
const DEFAULT_ALL_PRODUCTS_BG_OPTION = "1";

function getTitleStorageKey(code) {
  return `${ALL_PRODUCTS_TITLE_KEY_PREFIX}:${code}`;
}

function getFeaturesStorageKey(code) {
  return `${ALL_PRODUCTS_FEATURES_KEY_PREFIX}:${code}`;
}

function getProductNameStorageKey(code) {
  return `${ALL_PRODUCTS_PRODUCT_NAME_KEY_PREFIX}:${code}`;
}

/**
 * Thin context override for per-product edits (titles, features, product name).
 */
const ProductEditsProvider = ({code, children}) => {
  const parent = useEdits();
  const titleStorageKey = getTitleStorageKey(code);
  const featuresStorageKey = getFeaturesStorageKey(code);
  const productNameStorageKey = getProductNameStorageKey(code);
  const titleValue = parent.titles?.[titleStorageKey];
  const featuresValue = parent.features?.[featuresStorageKey];
  const productNameValue = parent.productNames?.[productNameStorageKey];

  const scopedTitles = titleValue !== undefined ? {main: titleValue} : {};
  const scopedFeatures = featuresValue !== undefined ? {main: featuresValue} : {};
  const scopedProductNames = productNameValue !== undefined ? {main: productNameValue} : {};

  return (
    <Edits.Provider
      value={{
        ...parent,
        titles: scopedTitles,
        features: scopedFeatures,
        productNames: scopedProductNames,
        editTitles: (value) => {
          parent.editTitles(value, titleStorageKey, true);
        },
        editFeatures: (value) => {
          parent.editFeatures(value, featuresStorageKey, true);
        },
        editProductName: (value) => {
          parent.editProductName(value, productNameStorageKey, true);
        },
        loadTitles: (value) => {
          const normalized = typeof value === "string" ? value : value?.main;
          if (normalized !== undefined) {
            parent.editTitles(normalized, titleStorageKey, true);
          }
        },
        loadFeatures: (value) => {
          const normalized = typeof value === "string" ? value : value?.main;
          if (normalized !== undefined) {
            parent.editFeatures(normalized, featuresStorageKey, true);
          }
        },
        loadProductNames: (value) => {
          const normalized = typeof value === "string" ? value : value?.main;
          if (normalized !== undefined) {
            parent.editProductName(normalized, productNameStorageKey, true);
          }
        },
      }}
    >
      {children}
    </Edits.Provider>
  );
};

const AllProductsWhatsNew = () => {
  const parentSetting = useSetting();
  const bgDerivation = useBgDerivation();
  // Reads straight from the shared React Query cache populated by App's
  // useBackgrounds call — no relay through useImages() needed.
  // Passive observer — App.js's useBackgrounds owns the fetch for this
  // (channel, type) tuple. Firing from here would bypass the channel-gate
  // in App.js if the component were ever rendered before the gate opened.
  const {allBgUrls} = useBgData(parentSetting.channel?.value, parentSetting.type?.value, {
    enabled: false,
  });
  // Published by App under the flag; null on the legacy path (#567, B3).
  const catalogIndex = useBgCatalogIndex(parentSetting.channel?.value, parentSetting.type?.value);
  const activeSet = useCurrentSet() || "whats-new";
  const templateSelection = useTemplateSelection();

  const productKeys = useMemo(
    () => getReleaseProductCodesForType(parentSetting.type?.value),
    [parentSetting.type?.value]
  );

  const productItems = useMemo(() => {
    const releaseTemplatesById = new Map(
      drawnClientTemplates("releases", templateSelection).map((template) => [template.id, template])
    );

    return productKeys.flatMap((code) => {
      const product = PRODUCTS.find((item) => item.value === code);
      if (!product) return [];

      // The set's banners and the ones added from other sets, without the
      // removed ones (libs/bannerComposition.ts): the same list every
      // product's own page of this set draws.
      const filteredTemplates = withoutHiddenTemplates(
        getFilteredReleaseTemplates({
          selectedSet: activeSet,
          layoutOption: parentSetting.layoutOption,
          productValue: code,
          extraTemplateIds: parentSetting.extraTemplates,
          selection: templateSelection,
        }),
        withSlotTemplateIds("releases", parentSetting.hiddenTemplates)
      );

      return filteredTemplates
        .map((template) => {
          const sourceTemplate = releaseTemplatesById.get(template.id);
          if (!sourceTemplate) return null;
          return {
            id: `${template.id}-${code}`,
            templateId: template.id,
            // The instance ids of the slot's designs: a saved order names the slot.
            orderIds: template.memberIds.map((memberId) => `${memberId}-${code}`),
            code,
            product,
            template: sourceTemplate,
          };
        })
        .filter(Boolean);
    });
  }, [
    productKeys,
    activeSet,
    parentSetting.layoutOption,
    parentSetting.extraTemplates,
    parentSetting.hiddenTemplates,
    templateSelection,
  ]);

  const templatesPerProduct = useMemo(() => {
    return productItems.reduce((acc, item) => {
      acc[item.code] = (acc[item.code] || 0) + 1;
      return acc;
    }, {});
  }, [productItems]);

  const bannerIds = useMemo(() => productItems.map((item) => item.id), [productItems]);

  // Per-product background selections live in the setting context so
  // DefaultScenePanel can read them and save per-product bgOption overrides.
  const {perProductBg, editPerProductBg} = parentSetting;
  const userTouchedCarouselRef = useRef(new Set());
  const activeLayout = String(+parentSetting.layoutOption || 1);
  const activeBg = String(parentSetting.bgOption || 1);
  // The background the URL names, or none: the same key useInitialScene
  // resolves the aggregate under, so both read one response.
  const {background: urlBackground} = useUrlBackground();

  // Two sources for the carousel's saved selections, one per path. With
  // bgDerivation on, the v2 channel-wide list must not be fetched at all on the
  // page path (#567) — the server runs the same two lookups in /resolved-all.
  const {overrides, isResolved: isOverridesResolved} = useDefaultSceneOverrides(
    parentSetting.channel,
    {enabled: !bgDerivation}
  );
  const resolvedAll = useResolvedDefaultScenesAll({
    channel: parentSetting.channel?.value,
    type: parentSetting.type?.value,
    set: activeSet,
    layout: activeLayout,
    background: urlBackground ?? "",
    enabled: bgDerivation,
  });
  const isResolved = bgDerivation
    ? resolvedAll.isSuccess || resolvedAll.isError
    : isOverridesResolved;
  // The tiles of each product's own page of this set (the server leaves out
  // the backgrounds an admin hid there). Absent from a server older than it.
  const bgOffers = bgDerivation ? resolvedAll.data?.bg_offers : undefined;

  // The backgrounds each product's carousel cycles through: the tiles of the
  // product's own page. Without them, every variation the product has an
  // image for — a catalog lookup under bgDerivation (#567, B3), else a scan of
  // the tileset filenames.
  const bgOptionsByProduct = useMemo(() => {
    if (bgOffers) {
      return Object.fromEntries(productKeys.map((code) => [code, bgOffers[code] ?? []]));
    }
    const variationCount = catalogIndex
      ? Math.max(0, ...Object.keys(catalogIndex).map(Number))
      : allBgUrls.length;
    const byProduct = {};
    productKeys.forEach((code) => {
      const options = [];
      for (let index = 1; index <= variationCount; index += 1) {
        const hasImage = catalogIndex
          ? Boolean(lookupBgImage(catalogIndex, index, code)?.url)
          : Boolean(findBgUrl(allBgUrls, index, `/${code}`));
        if (hasImage) options.push(String(index));
      }
      byProduct[code] = options;
    });
    return byProduct;
  }, [bgOffers, allBgUrls, catalogIndex, productKeys]);

  /**
   * Per-product `{row, map}` saved bgOptions. `row` is the product's own
   * best-matching override row (NOT its merged scene — a global bgOption must
   * not shadow the ALL editor's map); `map` is the ALL editor's
   * allProductsBgOptions on the global (set, layout, background) row.
   */
  const savedBgByProduct = useMemo(() => {
    // Each banner opens on the background its product's page opens on: the
    // background of the product's scene. A saved background number does not
    // pin it, as it does not pin the page (default-scenes-resolved.ts).
    if (bgOffers) {
      const scenes = resolvedAll.data?.scenes ?? {};
      return Object.fromEntries(
        productKeys.map((code) => {
          const opening = scenes[code]?.bgOption;
          return [
            code,
            {row: opening === undefined || opening === null ? null : String(opening), map: null},
          ];
        })
      );
    }
    if (bgDerivation) {
      const byRow = resolvedAll.data?.bg_options ?? {};
      const byMap = resolvedAll.data?.all_products_bg_options ?? {};
      return Object.fromEntries(
        productKeys.map((code) => [code, {row: byRow[code] ?? null, map: byMap[code] ?? null}])
      );
    }

    const globalRow = resolveOverride(
      overrides,
      parentSetting.channel?.value,
      "*",
      parentSetting.type?.value,
      activeSet,
      activeLayout,
      activeBg
    );
    const rawMap = globalRow?.scene_data?.[ALL_PRODUCTS_BG_OPTIONS_FIELD];
    return Object.fromEntries(
      productKeys.map((code) => {
        const row = resolveOverride(
          overrides,
          parentSetting.channel?.value,
          code,
          parentSetting.type?.value,
          activeSet,
          null,
          null
        );
        const rawFromMap = rawMap && typeof rawMap === "object" ? rawMap[code] : null;
        return [
          code,
          {
            row: row?.scene_data?.bgOption ? String(row.scene_data.bgOption) : null,
            map: rawFromMap !== undefined && rawFromMap !== null ? String(rawFromMap) : null,
          },
        ];
      })
    );
  }, [
    bgOffers,
    bgDerivation,
    resolvedAll.data,
    overrides,
    productKeys,
    parentSetting.channel,
    parentSetting.type,
    activeSet,
    activeLayout,
    activeBg,
  ]);

  // The background a banner shows when nothing else names one: the first tile
  // of the product's page, or (without the tiles) background 1 when the
  // product has it.
  const fallbackOptionOf = (options) =>
    bgOffers
      ? (options[0] ?? DEFAULT_ALL_PRODUCTS_BG_OPTION)
      : options.includes(DEFAULT_ALL_PRODUCTS_BG_OPTION)
        ? DEFAULT_ALL_PRODUCTS_BG_OPTION
        : options[0] || DEFAULT_ALL_PRODUCTS_BG_OPTION;

  // Initialize carousel from saved per-product bgOption overrides.
  useEffect(() => {
    if (!isResolved) {
      return;
    }

    editPerProductBg((prev) => {
      const next = {};
      let changed = false;

      productKeys.forEach((code) => {
        const options = bgOptionsByProduct[code] || [];
        const {row: savedBg = null, map: savedBgFromMap = null} = savedBgByProduct[code] || {};
        const fallbackOption = fallbackOptionOf(options);

        const current = prev[code];
        const isUserTouched = userTouchedCarouselRef.current.has(code);
        // Priority: current user selection > saved override > fallback
        const resolved =
          isUserTouched && current && options.includes(current)
            ? current
            : savedBg && options.includes(savedBg)
              ? savedBg
              : savedBgFromMap && options.includes(savedBgFromMap)
                ? savedBgFromMap
                : current && options.includes(current)
                  ? current
                  : fallbackOption;
        next[code] = resolved;
        if (resolved !== current) {
          changed = true;
        }
      });

      const hasDifferentShape = Object.keys(prev).length !== productKeys.length;
      if (!changed && !hasDifferentShape) {
        return prev;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fallbackOptionOf reads only bgOffers, which bgOptionsByProduct follows
  }, [productKeys, bgOptionsByProduct, editPerProductBg, isResolved, savedBgByProduct]);

  const cycleBackground = (code, direction) => {
    const options = bgOptionsByProduct[code] || [];
    if (options.length === 0) return;
    userTouchedCarouselRef.current.add(code);

    editPerProductBg((prev) => {
      const current = prev[code] && options.includes(prev[code]) ? prev[code] : options[0];
      const currentIndex = options.indexOf(current);
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      return {
        ...prev,
        [code]: options[nextIndex],
      };
    });
  };

  // The banners of a product show the look of the background its carousel
  // shows: that background's ready cut, base colour, fade, text colour and logo
  // from the product's scene (utils/allProductsScene.js). The aggregate gives
  // them the look of the background the product's scene was resolved under;
  // for another one, the product's scene under it replaces that look.
  const queryClient = useQueryClient();
  const {
    editCustomBgs,
    editBgOpacities,
    editBgBrightnesses,
    editTextColorLocked,
    editLogoVariant,
    editButtonColor,
  } = useImages();
  const basesRef = useRef(parentSetting.bases);
  basesRef.current = parentSetting.bases;
  const perProductBgRef = useRef(perProductBg);
  perProductBgRef.current = perProductBg;
  // code → the background whose look the banners of the product show. Each
  // time the default scene loads (useInitialScene gives the view new
  // `defaultTemplateValues`), the banners show the look the aggregate gave
  // them, so a carousel on another background gets its look again.
  const lookBgRef = useRef({});
  const resolvedScenes = bgOffers ? resolvedAll.data?.scenes : undefined;
  useEffect(() => {
    const scenes = resolvedScenes ?? {};
    lookBgRef.current = Object.fromEntries(
      productKeys.map((code) => [code, String(scenes[code]?.bgOption ?? "")])
    );
  }, [resolvedScenes, productKeys, parentSetting.defaultTemplateValues]);
  useEffect(() => {
    if (!resolvedScenes) return;
    const templateIdsByCode = {};
    for (const item of productItems) {
      (templateIdsByCode[item.code] ??= []).push(item.templateId);
    }
    for (const [code, templateIds] of Object.entries(templateIdsByCode)) {
      const shown = perProductBg[code];
      if (!shown || lookBgRef.current[code] === shown) continue;
      lookBgRef.current[code] = shown;
      const options = resolvedDefaultScenesAllQueryOptions({
        channel: parentSetting.channel?.value,
        type: parentSetting.type?.value,
        set: activeSet,
        layout: activeLayout,
        background: shown,
      });
      queryClient.fetchQuery(options).then(
        (data) => applyLook(code, shown, templateIds, data),
        (error) => {
          // Not applied: the next change of the carousel tries again.
          if (lookBgRef.current[code] === shown) delete lookBgRef.current[code];
          throw error;
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyLook reads the latest state through refs
  }, [
    resolvedScenes,
    perProductBg,
    productItems,
    activeSet,
    activeLayout,
    queryClient,
    parentSetting.defaultTemplateValues,
  ]);

  /** Give the banners of `code` the look of background `shown` from `data`. */
  function applyLook(code, shown, templateIds, data) {
    // A later click or a new aggregate decides the look now.
    if (perProductBgRef.current[code] !== shown || lookBgRef.current[code] !== shown) return;
    const look = productInstanceAppearance(data.scenes?.[code] ?? null, code, templateIds);
    const instanceIds = templateIds.map((templateId) => `${templateId}-${code}`);
    const bases = {...basesRef.current};
    for (const id of instanceIds) {
      editCustomBgs(id, look.customBgs?.[id] ?? null, true);
      editBgOpacities(id, look.bgOpacities?.[id]);
      editBgBrightnesses(id, look.bgBrightnesses?.[id]);
      editTextColorLocked(id, look.textColorLocked?.[id]);
      editLogoVariant(id, look.logoVariants?.[id]);
      editButtonColor(id, look.buttonColors?.[id]);
      if (look.bases?.[id] === undefined) delete bases[id];
      else bases[id] = look.bases[id];
    }
    parentSetting.editBases(bases);
  }

  // The ALL view keeps the texts of each product in its own keys
  // (ProductEditsProvider), so the banners have no text mode here, as they
  // have no chain: a default that gives a banner a text must not lock it.
  const templateModesWithoutText = useMemo(
    () => withoutDimensionModes(parentSetting.templateModes, "text"),
    [parentSetting.templateModes]
  );

  // Every banner of a product reads that product's title, features and name,
  // the banners added from other sets too (libs/bannerComposition.ts): the
  // shared `main` texts of this view belong to no product.
  const renderTemplateItem = (item) => {
    const templateNode = item.template.getComponent.call({...item.template, id: item.id});
    return <ProductEditsProvider code={item.code}>{templateNode}</ProductEditsProvider>;
  };

  return (
    <SortableList
      items={productItems}
      useDragHandle={true}
      className="all-products-whats-new"
      containerStyle={{flexDirection: "column", flexWrap: "nowrap", gap: "14px", padding: "8px 0"}}
      getItemClassName={(item) =>
        cn("template-wrap", "all-products-template-wrap", {
          hidden: !parentSetting.visible.some((x) => x === item.id),
        })
      }
      // The base colour of the product's background, as on the Releases page.
      getItemStyle={(item) => {
        const base = parentSetting.bases?.[item.id];
        return base ? {"--base-color": `#${base}`} : undefined;
      }}
      renderItem={(item, {listeners, attributes} = {}) => {
        const {code, product} = item;
        const options = bgOptionsByProduct[code] || [];
        const bgOpt = perProductBg[code] || fallbackOptionOf(options);
        const isCarouselDisabled = options.length <= 1;
        const productLabel = product.shortLabel || product.label;
        const isMultiTemplateProduct = (templatesPerProduct[code] || 0) > 1;

        return (
          // Setting.Provider overrides `product` per child (drives template
          // styling: logos, accent colors, layouts). The RQ cache is keyed
          // on (channel, type) only (see useBgData / #251), so the child's
          // useBgUrl call shares the parent's tile-set cache entry without
          // any extra wiring.
          <Setting.Provider
            value={{
              ...parentSetting,
              product,
              bgOption: bgOpt,
              locked: [],
              templateModes: templateModesWithoutText,
            }}
          >
            <div
              className="template-annotation"
              {...(listeners || {})}
              {...(attributes || {})}
              style={{cursor: "grab"}}
            >
              <span className="label">
                {isMultiTemplateProduct ? `${productLabel} · ${item.template.label}` : productLabel}
              </span>
              <TemplateControls
                id={item.id}
                templateChannel="releases"
                hasOnlyOneBanner={bannerIds.length === 1}
                bannerIds={bannerIds}
                hideLock={true}
              />
            </div>
            <div className="all-products-template-content">{renderTemplateItem(item)}</div>
            <div className="bg-carousel-controls">
              <button
                type="button"
                className="bg-carousel-control prev"
                onClick={() => cycleBackground(code, -1)}
                disabled={isCarouselDisabled}
                aria-label={`Previous background for ${product.label}`}
              >
                <span>{"<"}</span>
              </button>
              <button
                type="button"
                className="bg-carousel-control next"
                onClick={() => cycleBackground(code, 1)}
                disabled={isCarouselDisabled}
                aria-label={`Next background for ${product.label}`}
              >
                <span>{">"}</span>
              </button>
            </div>
          </Setting.Provider>
        );
      }}
    />
  );
};

export default AllProductsWhatsNew;
